/**
 * /messages — member ↔ member direct messaging ("Registry Chat").
 * Opt-out model: toggle in the header. Blocks + report per thread.
 * ?to={userId} starts/opens a conversation; ?thread={id} deep-links.
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ChatCircleDotsIcon as ChatCircleDots, PaperPlaneRightIcon as PaperPlaneRight, ArrowLeftIcon as ArrowLeft, ProhibitIcon as Prohibit, FlagIcon as Flag, WarningIcon as Warning } from "@phosphor-icons/react";

export default function Messages() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [threads, setThreads] = useState(null);
  const [active, setActive] = useState(params.get("thread") || null);
  const [toUser, setToUser] = useState(params.get("to") || null);
  const [enabled, setEnabled] = useState(true);

  const loadThreads = useCallback(() => {
    api.get("/chat/threads").then((r) => setThreads(r.data?.threads || [])).catch(() => setThreads([]));
  }, []);

  useEffect(() => {
    loadThreads();
    api.get("/chat/settings").then((r) => setEnabled(r.data?.enabled !== false)).catch(() => {});
  }, [loadThreads]);

  const toggleEnabled = async (v) => {
    setEnabled(v);
    try {
      await api.post("/chat/settings", { enabled: v });
      toast.success(v ? "Members can message you." : "Direct messages turned off.");
    } catch { toast.error("Could not save setting."); }
  };

  const openThread = (id) => {
    setActive(id);
    setToUser(null);
    setParams((p) => { const n = new URLSearchParams(p); n.set("thread", id); n.delete("to"); return n; }, { replace: true });
  };

  const showList = !active && !toUser;

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto" data-testid="messages-page">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-6 pb-5 border-b border-[#E5E5E5]">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-2 flex items-center gap-2">
              <ChatCircleDots size={14} weight="fill" /> Registry Chat
            </p>
            <h1 className="font-spectral font-light text-4xl tracking-tight">Messages</h1>
          </div>
          <label className="flex items-center gap-2 text-[12px] text-[#5C5C5C]">
            Allow members to message me
            <Switch checked={enabled} onCheckedChange={toggleEnabled} data-testid="chat-enabled-toggle" />
          </label>
        </div>

        {showList && (
          <NewMessageBar onPick={(id) => { setToUser(id); setActive(null); }} />
        )}

        {(active || toUser) ? (
          <ThreadView
            key={active || toUser}
            threadId={active}
            toUserId={toUser}
            me={user?.id}
            onBack={() => { setActive(null); setToUser(null); setParams({}, { replace: true }); loadThreads(); }}
            onThreadCreated={openThread}
          />
        ) : null}

        {showList && (
          <div className="space-y-2" data-testid="chat-thread-list">
            {threads === null ? (
              <div className="h-20 bg-[#FBF7EC] rounded-xl animate-pulse" />
            ) : threads.length === 0 ? (
              <p className="text-[13px] text-[#8B8B8B] py-8 text-center" data-testid="chat-empty-state">
                No conversations yet. Search for a member above, or tap
                &ldquo;Message&rdquo; on any member&rsquo;s profile.
              </p>
            ) : (
              threads.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => openThread(t.id)}
                  className="w-full text-left rounded-xl border border-[#EAEAEA] hover:border-ink/40 bg-white p-4 flex items-center gap-3 transition-all"
                  data-testid={`chat-thread-${t.id}`}
                >
                  <span className="flex-1 min-w-0">
                    <span className="block text-[13px] font-semibold text-ink truncate">{t.other?.name}</span>
                    <span className="block text-[12px] text-[#6B6B6B] truncate">{t.last_message_preview}</span>
                  </span>
                  {t.unread > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#C1272D] text-white text-[10px] font-bold">{t.unread}</span>
                  )}
                  <span className="text-[10px] text-[#9A9A9A] flex-shrink-0">
                    {t.last_message_at ? new Date(t.last_message_at).toLocaleDateString() : ""}
                  </span>
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function NewMessageBar({ onPick }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);

  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) { setResults(null); return undefined; }
    const t = setTimeout(() => {
      api.get("/chat/member-search", { params: { q: query } })
        .then((r) => setResults(r.data?.members || []))
        .catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="mb-5" data-testid="chat-new-message-bar">
      <div className="flex items-center gap-2 rounded-full border-2 border-[#0E0E0E] bg-white px-4 py-2.5 focus-within:border-crimson transition-colors">
        <PaperPlaneRight size={16} weight="duotone" className="text-crimson flex-shrink-0" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Start a new message — search members by name…"
          className="flex-1 outline-none text-[14px] bg-transparent"
          data-testid="chat-member-search-input"
        />
      </div>
      {results !== null && (
        <div className="mt-2 rounded-xl border border-[#EAEAEA] bg-white overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.06)]">
          {results.length === 0 ? (
            <p className="text-[12px] text-[#8B8B8B] px-4 py-3">
              No members found — they may have chat turned off, or try a different spelling.
            </p>
          ) : (
            results.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => { onPick(m.id); setQ(""); setResults(null); }}
                className="w-full text-left px-4 py-2.5 flex items-center gap-2 hover:bg-[#FBF7EC] transition-colors border-b border-[#F5F5F5] last:border-none"
                data-testid={`chat-member-result-${m.id}`}
              >
                <span className="text-[13px] font-semibold text-ink flex-1">{m.name}</span>
                {m.persona && (
                  <span className="text-[9px] font-bold uppercase tracking-wider bg-[#FBF7EC] text-[#8A6A00] border border-[#E8D9A6] rounded-full px-1.5 py-0.5">
                    {String(m.persona).replaceAll("_", " ")}
                  </span>
                )}
                <span className="text-[11px] text-crimson font-bold">Message →</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function ThreadView({ threadId, toUserId, me, onBack, onThreadCreated }) {
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  const load = useCallback(() => {
    if (!threadId) return;
    api.get(`/chat/threads/${threadId}`).then((r) => setThread(r.data)).catch(() => {});
    api.get(`/chat/threads/${threadId}/messages`).then((r) => setMessages(r.data?.messages || [])).catch(() => {});
    api.post(`/chat/threads/${threadId}/read`).catch(() => {});
  }, [threadId]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, [load]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ block: "end" }); }, [messages.length]);

  const send = async () => {
    const text = body.trim();
    if (!text) return;
    setBusy(true);
    try {
      if (threadId) {
        const r = await api.post(`/chat/threads/${threadId}/messages`, { body: text });
        setMessages((m) => [...m, r.data.message]);
      } else {
        const r = await api.post("/chat/start", { recipient_user_id: toUserId, body: text });
        onThreadCreated(r.data.thread_id);
      }
      setBody("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Message not sent.");
    } finally { setBusy(false); }
  };

  const blockOther = async () => {
    const other = thread?.other;
    if (!other?.id) return;
    if (!window.confirm(`Block ${other.name}? Neither of you will be able to message the other.`)) return;
    try {
      await api.post(`/users/${other.id}/block`);
      toast.success(`${other.name} blocked.`);
      onBack();
    } catch (e) { toast.error(e?.response?.data?.detail || "Block failed."); }
  };

  const report = async () => {
    const reason = window.prompt("What's wrong with this conversation? (sent to the Registry team)");
    if (!reason || reason.trim().length < 3) return;
    try {
      await api.post(`/chat/threads/${threadId}/report`, { reason: reason.trim() });
      toast.success("Reported. Our team will review this conversation.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Report failed."); }
  };

  return (
    <div className="bg-white border border-[#EAEAEA] rounded-2xl flex flex-col" style={{ minHeight: "60vh" }} data-testid="chat-thread-view">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#F0F0F0]">
        <button type="button" onClick={onBack} className="text-[#6B6B6B] hover:text-ink" data-testid="chat-back-btn">
          <ArrowLeft size={16} weight="bold" />
        </button>
        <span className="flex-1 text-[14px] font-semibold text-ink truncate">
          {thread?.other?.name || "New conversation"}
        </span>
        {threadId && (
          <>
            <button type="button" onClick={report} title="Report conversation" className="text-[#8A6A00] hover:text-crimson" data-testid="chat-report-btn">
              <Flag size={16} weight="duotone" />
            </button>
            <button type="button" onClick={blockOther} title="Block member" className="text-[#C1272D] hover:text-ink" data-testid="chat-block-btn">
              <Prohibit size={16} weight="duotone" />
            </button>
          </>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" style={{ maxHeight: "55vh" }}>
        {messages.map((m) => {
          const mine = m.sender_id === me;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%]`}>
                <div className={`rounded-2xl px-3.5 py-2 text-[13px] leading-relaxed whitespace-pre-wrap ${mine ? "bg-[#FBF7EC] border border-[#E8D9A6] text-ink" : "bg-[#F5F5F5] text-ink"}`}>
                  {m.body}
                </div>
                {(m.scam_warning || m.pii_warning) && (
                  <div className="flex items-start gap-1.5 mt-1 text-[11px] text-[#C1272D]" data-testid={`chat-warning-${m.id}`}>
                    <Warning size={13} weight="fill" className="mt-0.5 flex-shrink-0" />
                    <span>
                      {m.scam_warning
                        ? "Caution: never wire money, buy gift cards, or pay outside the Registry — those payments can't be protected or recovered."
                        : "This message shares contact details. Keep conversations (and payments) on the Registry for your protection."}
                    </span>
                  </div>
                )}
                <div className={`text-[10px] text-[#B0B0B0] mt-0.5 ${mine ? "text-right" : ""}`}>
                  {new Date(m.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <div className="border-t border-[#F0F0F0] p-3 flex items-end gap-2">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Write a message…"
          rows={2}
          className="flex-1 resize-none"
          data-testid="chat-composer"
        />
        <Button onClick={send} disabled={busy || !body.trim()} className="rounded-full bg-crimson hover:bg-ink text-white h-10 w-10 p-0" data-testid="chat-send-btn">
          <PaperPlaneRight size={16} weight="fill" />
        </Button>
      </div>
    </div>
  );
}
